import { useState } from 'react';
import { Link } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import Footer from '../../common/Footer';
import Header from '../../common/Header';
import SeoHelmet from '../../common/SeoHelmet';

const SlopeStabilityTheory = () => {
  const [showChangelog, setShowChangelog] = useState(false);
  const alertDarkTextSx = {
    '& .MuiAlert-message': {
      color: (theme) => (theme.palette.mode === 'dark' ? 'var(--darkModeTextColor)' : 'inherit'),
      lineHeight: 1.85,
      width: '100%',
    },
    '& .MuiAlertTitle-root': {
      color: (theme) => (theme.palette.mode === 'dark' ? 'var(--darkModeTextColor)' : 'inherit'),
    },
  };

  const IconLi = ({ children, icon = 'check' }) => {
    const IconComponent =
      icon === 'arrow' ? ArrowRightAltIcon : icon === 'book' ? MenuBookOutlinedIcon : CheckCircleOutlineIcon;
    const iconColor = icon === 'arrow' ? 'info.main' : icon === 'book' ? 'warning.main' : 'success.main';

    return (
      <Box component="li" sx={{ listStyle: 'none', display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
        <IconComponent sx={{ fontSize: 18, color: iconColor, mt: '3px', flexShrink: 0 }} />
        <Box component="span">{children}</Box>
      </Box>
    );
  };

  return (
    <>
      <SeoHelmet
        title="GeoCalc | Slope Stability | Theory"
        description="Theory and practical usage notes for GeoCalc slope stability: Bishop Simplified workflow, single-surface and grid-search modes, filters, equations, and output interpretation."
        url="https://www.geocalc.app/slope-stability-theory"
        image="https://www.geocalc.app/assets/og-theory-v1.jpg"
        schemaType="page"
        breadcrumbs={[
          { name: 'Home', url: 'https://www.geocalc.app/' },
          { name: 'Slope Stability | Theory', url: 'https://www.geocalc.app/slope-stability-theory' },
        ]}
      />
      <Header />
      <div className="theory">
        <div className="theoryContainer">
          <h1>Slope Stability | Theory and User Guide</h1>

          <Alert severity="success" variant="outlined" sx={{ ...alertDarkTextSx, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
              <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
                <AlertTitle sx={{ fontWeight: 700, mb: 0.5 }}>Changelog</AlertTitle>
                Slope Stability release line starts at <strong>1.0.0</strong>. Latest patch: <strong>1.0.1</strong> (March 10, 2026).
              </Box>
              <IconButton
                size="small"
                aria-label={showChangelog ? 'Hide changelog' : 'Show changelog'}
                onClick={() => setShowChangelog((prev) => !prev)}
                sx={{
                  mt: '-2px',
                  transform: showChangelog ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.15s ease',
                }}
              >
                <ExpandMoreIcon fontSize="small" />
              </IconButton>
            </Box>
            <Collapse in={showChangelog} timeout="auto" unmountOnExit>
              <Stack spacing={1.5} sx={{ mt: 1.25 }}>
                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    px: 1.25,
                    py: 1,
                    bgcolor: 'background.paper',
                  }}
                >
                  <Box sx={{ fontWeight: 700, mb: 0.25 }}>Version 1.0.1 | Patch update</Box>
                  <Box sx={{ typography: 'caption', color: 'text.secondary', mb: 0.75 }}>Released on March 10, 2026</Box>
                  <Box component="ul" sx={{ pl: 0, my: 0 }}>
                    <IconLi>Added dark mode support to the slope stability sketch.</IconLi>
                    <IconLi>Improved dark-mode colors for the grid, labels, ground profile, water table, search area, legend, badges, and slip surfaces.</IconLi>
                    <IconLi>Added a download button so users can save the sketch as a PNG.</IconLi>
                    <IconLi>Improved the downloaded sketch so the text matches the on-screen sketch more closely.</IconLi>
                    <IconLi>Added click-to-pin behavior for search hover surfaces.</IconLi>
                    <IconLi>Users can click a hovered search surface to keep it visible on the sketch, click it again to remove it, or click another surface to replace it.</IconLi>
                    <IconLi>Fixed the export so pinned surfaces are included in the downloaded image.</IconLi>
                  </Box>
                </Box>
                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    px: 1.25,
                    py: 1,
                    bgcolor: 'background.paper',
                  }}
                >
                  <Box sx={{ fontWeight: 700, mb: 0.25 }}>Version 1.0.0 | Base release</Box>
                  <Box sx={{ typography: 'caption', color: 'text.secondary', mb: 0.75 }}>Released on March 5, 2026</Box>
                  <Box component="ul" sx={{ pl: 0, my: 0 }}>
                    <IconLi>Initial public release of the Slope Stability tool and theory page.</IconLi>
                  </Box>
                </Box>
              </Stack>
            </Collapse>
          </Alert>

          <Alert severity="info" variant="outlined" sx={{ ...alertDarkTextSx, mb: 2 }}>
            <AlertTitle sx={{ fontWeight: 700 }}>Method Availability</AlertTitle>
            <p>
              GeoCalc slope-stability calculations currently use the methods listed below:
            </p>
            <Box component="ul" sx={{ pl: 0, my: 1 }}>
              <IconLi>
                <strong>Bishop Simplified</strong>: available now for calculation.
              </IconLi>
              <IconLi icon="arrow">
                <strong>Janbu Simplified</strong>: planned for future versions and not yet implemented.
              </IconLi>
            </Box>
          </Alert>

          <Alert severity="info" variant="outlined" sx={{ ...alertDarkTextSx, mb: 2 }}>
            <AlertTitle sx={{ fontWeight: 700 }}>1) What This Calculator Solves</AlertTitle>
            <p>The tool currently solves the following problem definition:</p>
            <Box component="ul" sx={{ pl: 0, my: 1 }}>
              <IconLi>2D slope-stability analysis with vertical slices.</IconLi>
              <IconLi>Circular slip surfaces.</IconLi>
              <IconLi>Single drained soil material for the full section.</IconLi>
              <IconLi>Horizontal groundwater line.</IconLi>
              <IconLi>
                Two workflows: <strong>Single Surface</strong> and <strong>Grid Search</strong>.
              </IconLi>
            </Box>
          </Alert>

          <Alert severity="info" variant="outlined" sx={{ ...alertDarkTextSx, mb: 2 }}>
            <AlertTitle sx={{ fontWeight: 700 }}>2) Core Equations (Bishop Simplified)</AlertTitle>
            <p>
              Bishop Simplified estimates the factor of safety by balancing resisting and driving components over all slices.
            </p>
            <p className="theoryEquation">
              <math display="block">
                <mrow>
                  <msub>
                    <mi>F</mi>
                    <mi>s</mi>
                  </msub>
                  <mo>=</mo>
                  <mfrac>
                    <mrow>
                      <mo>&#x2211;</mo>
                      <mfrac>
                        <mrow>
                          <msub>
                            <mi>c</mi>
                            <mi>i</mi>
                          </msub>
                          <mo>&#x2032;</mo>
                          <msub>
                            <mi>L</mi>
                            <mi>i</mi>
                          </msub>
                          <mo>+</mo>
                          <mo>(</mo>
                          <msub>
                            <mi>W</mi>
                            <mi>i</mi>
                          </msub>
                          <mo>-</mo>
                          <msub>
                            <mi>u</mi>
                            <mi>i</mi>
                          </msub>
                          <msub>
                            <mi>L</mi>
                            <mi>i</mi>
                          </msub>
                          <mo>)</mo>
                          <mi>tan</mi>
                          <mo>(</mo>
                          <msub>
                            <mi>&phi;</mi>
                            <mi>i</mi>
                          </msub>
                          <mo>&#x2032;</mo>
                          <mo>)</mo>
                        </mrow>
                        <mrow>
                          <msub>
                            <mi>m</mi>
                            <mrow>
                              <mi>&alpha;</mi>
                              <mo>,</mo>
                              <mi>i</mi>
                            </mrow>
                          </msub>
                        </mrow>
                      </mfrac>
                    </mrow>
                    <mrow>
                      <mo>|</mo>
                      <mo>&#x2211;</mo>
                      <msub>
                        <mi>W</mi>
                        <mi>i</mi>
                      </msub>
                      <mi>sin</mi>
                      <mo>(</mo>
                      <msub>
                        <mi>&alpha;</mi>
                        <mi>i</mi>
                      </msub>
                      <mo>)</mo>
                      <mo>|</mo>
                    </mrow>
                  </mfrac>
                </mrow>
              </math>
            </p>
            <p className="theoryEquation">
              <math display="block">
                <mrow>
                  <msub>
                    <mi>m</mi>
                    <mrow>
                      <mi>&alpha;</mi>
                      <mo>,</mo>
                      <mi>i</mi>
                    </mrow>
                  </msub>
                  <mo>=</mo>
                  <mi>cos</mi>
                  <mo>(</mo>
                  <msub>
                    <mi>&alpha;</mi>
                    <mi>i</mi>
                  </msub>
                  <mo>)</mo>
                  <mo>-</mo>
                  <mfrac>
                    <mrow>
                      <mi>sin</mi>
                      <mo>(</mo>
                      <msub>
                        <mi>&alpha;</mi>
                        <mi>i</mi>
                      </msub>
                      <mo>)</mo>
                      <mi>tan</mi>
                      <mo>(</mo>
                      <msub>
                        <mi>&phi;</mi>
                        <mi>i</mi>
                      </msub>
                      <mo>&#x2032;</mo>
                      <mo>)</mo>
                    </mrow>
                    <mrow>
                      <msub>
                        <mi>F</mi>
                        <mi>s</mi>
                      </msub>
                    </mrow>
                  </mfrac>
                </mrow>
              </math>
            </p>
            <p>For a horizontal groundwater table:</p>
            <p className="theoryEquation">
              <math display="block">
                <mrow>
                  <msub>
                    <mi>u</mi>
                    <mi>i</mi>
                  </msub>
                  <mo>=</mo>
                  <msub>
                    <mi>&gamma;</mi>
                    <mi>w</mi>
                  </msub>
                  <mo>&#x00D7;</mo>
                  <mi>max</mi>
                  <mo>(</mo>
                  <msub>
                    <mi>y</mi>
                    <mi>w</mi>
                  </msub>
                  <mo>-</mo>
                  <msub>
                    <mi>y</mi>
                    <mrow>
                      <mi>b</mi>
                      <mo>,</mo>
                      <mi>i</mi>
                    </mrow>
                  </msub>
                  <mo>,</mo>
                  <mn>0</mn>
                  <mo>)</mo>
                </mrow>
              </math>
            </p>
            <p>The equation is solved iteratively until the change in F<sub>s</sub> is within the selected tolerance.</p>
            <Box component="ul" sx={{ pl: 0, my: 1 }}>
              <IconLi>
                <strong>L</strong>: slice base length.
              </IconLi>
              <IconLi>
                <strong>W</strong>: slice weight.
              </IconLi>
              <IconLi>
                <strong>u</strong>: pore-water pressure at slice base.
              </IconLi>
              <IconLi>
                <strong>c&apos;</strong>, <strong>&phi;&apos;</strong>: effective Mohr-Coulomb strength parameters.
              </IconLi>
            </Box>
          </Alert>

          <Alert severity="info" variant="outlined" sx={{ ...alertDarkTextSx, mb: 2 }}>
            <AlertTitle sx={{ fontWeight: 700 }}>3) Input Tabs Explained</AlertTitle>
            <p>Each input tab controls a different part of the analysis setup.</p>
            <Stack spacing={2} sx={{ mt: 1.5 }}>
              <Alert severity="success" variant="outlined" sx={alertDarkTextSx}>
                <AlertTitle sx={{ fontWeight: 700 }}>General Tab</AlertTitle>
                <Box component="ul" sx={{ pl: 0, my: 1 }}>
                  <IconLi>Case name for saving and project organization.</IconLi>
                  <IconLi>Method list (multi-select). Current calculations use Bishop Simplified.</IconLi>
                  <IconLi>
                    Debug Slices switch: enabled only in Single Surface mode. When turned on, slice-by-slice force data is
                    returned in the output.
                  </IconLi>
                  <IconLi>Heatmap palette selector in Grid Search mode: Default or Colorblind-safe.</IconLi>
                </Box>
              </Alert>
              <Alert severity="success" variant="outlined" sx={alertDarkTextSx}>
                <AlertTitle sx={{ fontWeight: 700 }}>Ground Profile Tab</AlertTitle>
                <Box component="ul" sx={{ pl: 0, my: 1 }}>
                  <IconLi>
                    Enter profile points P<sub>1</sub>..P<sub>n</sub> with strictly increasing x values.
                  </IconLi>
                  <IconLi>You can define between 2 and 50 points.</IconLi>
                </Box>
              </Alert>
              <Alert severity="success" variant="outlined" sx={alertDarkTextSx}>
                <AlertTitle sx={{ fontWeight: 700 }}>Material and Ground Water Tabs</AlertTitle>
                <Box component="ul" sx={{ pl: 0, my: 1 }}>
                  <IconLi>
                    Material inputs: name, c&apos; (kPa), &phi;&apos; (deg), &gamma;<sub>dry</sub> (kN/m<sup>3</sup>), &gamma;
                    <sub>sat</sub> (kN/m<sup>3</sup>).
                  </IconLi>
                  <IconLi>
                    Water inputs: elevation (m) and &gamma;<sub>w</sub> (kN/m<sup>3</sup>).
                  </IconLi>
                </Box>
              </Alert>
              <Alert severity="success" variant="outlined" sx={alertDarkTextSx}>
                <AlertTitle sx={{ fontWeight: 700 }}>Mode and Search Tab</AlertTitle>
                <Box component="ul" sx={{ pl: 0, my: 1 }}>
                  <IconLi>Single Surface mode: solve one circle using center x, center y, and radius.</IconLi>
                  <IconLi>Grid Search mode: define min/max/count for center x, center y, and radius.</IconLi>
                  <IconLi>
                    Surface filters are active in both modes: Minimum Arc Elevation, Failure Direction, Allow Reverse Curvature,
                    Left Limit X, Right Limit X.
                  </IconLi>
                </Box>
              </Alert>
              <Alert severity="success" variant="outlined" sx={alertDarkTextSx}>
                <AlertTitle sx={{ fontWeight: 700 }}>Options Tab</AlertTitle>
                <Box component="ul" sx={{ pl: 0, my: 1 }}>
                  <IconLi>
                    Numerical settings: slice count, tolerance, max iterations, max reported candidates, and minimum valid m
                    <sub>&alpha;</sub>.
                  </IconLi>
                </Box>
              </Alert>
            </Stack>
          </Alert>

          <Alert severity="info" variant="outlined" sx={{ ...alertDarkTextSx, mb: 2 }}>
            <AlertTitle sx={{ fontWeight: 700 }}>4) Surface Filtering and Search Logic</AlertTitle>
            <p>Candidate circles are generated from search ranges and filtered before solving.</p>
            <p>
              In Grid Search mode, candidate circles are generated on a deterministic 3D grid from center x, center y, and radius
              ranges. The count values are number of sampled points on each axis.
            </p>
            <p>Before solving, each candidate is screened in this order:</p>
            <Box component="ul" sx={{ pl: 0, my: 1 }}>
              <IconLi icon="arrow">reverse-curvature check,</IconLi>
              <IconLi icon="arrow">ground-profile intersection check,</IconLi>
              <IconLi icon="arrow">
                slope-limit check (x<sub>left</sub> and x<sub>right</sub>),
              </IconLi>
              <IconLi icon="arrow">failure-direction check,</IconLi>
              <IconLi icon="arrow">arc-below-surface check,</IconLi>
              <IconLi icon="arrow">minimum arc-elevation check.</IconLi>
            </Box>
          </Alert>

          <Alert severity="info" variant="outlined" sx={{ ...alertDarkTextSx, mb: 2 }}>
            <AlertTitle sx={{ fontWeight: 700 }}>5) How Water and Unit Weights Are Handled</AlertTitle>
            <p>Groundwater and unit weights are handled with the following assumptions.</p>
            <Box component="ul" sx={{ pl: 0, my: 1 }}>
              <IconLi>Pore pressure is calculated from the horizontal water line at the slice base.</IconLi>
              <IconLi>
                Current self-weight integration uses one total unit weight based on &gamma;<sub>dry</sub>.
              </IconLi>
              <IconLi>
                &gamma;<sub>sat</sub> is stored for forward compatibility with future model upgrades.
              </IconLi>
            </Box>
          </Alert>

          <Alert severity="info" variant="outlined" sx={{ ...alertDarkTextSx, mb: 2 }}>
            <AlertTitle sx={{ fontWeight: 700 }}>6) Sketch and Visual Output</AlertTitle>
            <p>The sketch displays geometry, search region, and stability results with the elements below.</p>
            <Box component="ul" sx={{ pl: 0, my: 1 }}>
              <IconLi>Ground profile, soil polygon, water table line, and profile points are always shown.</IconLi>
              <IconLi>Mode badge and failure-direction badge are shown inside the sketch panel.</IconLi>
              <IconLi>Slope-limit guide lines are drawn when limits are available.</IconLi>
              <IconLi>
                The critical surface is drawn as the underground arc segment, with radius lines from arc endpoints to circle
                center.
              </IconLi>
              <IconLi>
                Grid Search shows a center-range rectangle plus F<sub>s</sub> heat cells. Hovering a heat cell shows that
                cell&apos;s best surface while keeping the minimum-F<sub>s</sub> arc visible.
              </IconLi>
              <IconLi>Heatmap legend uses dynamic capping and shows a plus sign at capped top values (for example, 6.000+).</IconLi>
            </Box>
          </Alert>

          <Alert severity="info" variant="outlined" sx={{ ...alertDarkTextSx, mb: 2 }}>
            <AlertTitle sx={{ fontWeight: 700 }}>7) Result Summary Sections</AlertTitle>
            <p>Result cards report the following analysis outputs.</p>
            <p>
              Practical F<sub>s</sub> reading: values below 1.0 indicate instability, values near 1.0 indicate limit equilibrium,
              and larger values indicate increasing margin of safety. Project criteria may require higher minimum values.
            </p>
            <Box component="ul" sx={{ pl: 0, my: 1 }}>
              <IconLi>
                Critical Surface: F<sub>s</sub>, convergence status, iteration count, closure mode.
              </IconLi>
              <IconLi>Search Diagnostics: total candidates, valid candidates, and skip counts by rejection stage.</IconLi>
              <IconLi>Active Surface Filters: resolved values that were actually applied in the run.</IconLi>
              <IconLi>
                Slice Diagnostics table: shown for Single Surface runs when Debug Slices is enabled and returned by backend.
              </IconLi>
              <IconLi>Analysis Notes: warnings such as no valid surface or convergence/threshold notices.</IconLi>
            </Box>
          </Alert>

          <Alert severity="info" variant="outlined" sx={{ ...alertDarkTextSx, mb: 2 }}>
            <AlertTitle sx={{ fontWeight: 700 }}>8) Practical Coverage and Limits</AlertTitle>
            <p>Current model boundaries and coverage limits are listed below.</p>
            <Box component="ul" sx={{ pl: 0, my: 1 }}>
              <IconLi>Input guard limits are enforced in both frontend and backend for stability and server safety.</IconLi>
              <IconLi>Single Surface and Grid Search use the same solver and same filter logic.</IconLi>
              <IconLi>
                Current implementation does not yet include layered materials, non-horizontal phreatic lines, or non-circular
                methods.
              </IconLi>
            </Box>
          </Alert>

          <Alert severity="info" variant="outlined" sx={{ ...alertDarkTextSx, mb: 2 }}>
            <AlertTitle sx={{ fontWeight: 700 }}>9) References</AlertTitle>
            <p>Primary references for method formulation, engineering context, and software-style interpretation are listed below.</p>
            <Box component="ul" sx={{ pl: 0, my: 1 }}>
              <IconLi icon="book">
                Bishop, A.W. (1955). The use of the slip circle in the stability analysis of slopes. Geotechnique, 5(1), 7-17.
                DOI: <a href="https://doi.org/10.1680/geot.1955.5.1.7" target="_blank" rel="noreferrer">10.1680/geot.1955.5.1.7</a>
              </IconLi>
              <IconLi icon="book">
                Fredlund, D.G., and Krahn, J. (1977). Comparison of slope stability methods of analysis. Canadian Geotechnical
                Journal, 14(3), 429-439. DOI: <a href="https://doi.org/10.1139/t77-045" target="_blank" rel="noreferrer">10.1139/t77-045</a>
              </IconLi>
              <IconLi icon="book">
                Duncan, J.M., Wright, S.G., and Brandon, T.L. Soil Strength and Slope Stability (2nd ed.). Wiley.
              </IconLi>
              <IconLi icon="book">
                Rocscience Slide2 Help: Documentation and theory overview.{' '}
                <a
                  href="https://www.rocscience.com/help/slide2/overview/documentation-and-theory-overview"
                  target="_blank"
                  rel="noreferrer"
                >
                  www.rocscience.com/help/slide2/overview/documentation-and-theory-overview
                </a>
              </IconLi>
              <IconLi icon="book">
                Rocscience Slide2 Help: Slip surfaces and factor of safety.{' '}
                <a
                  href="https://www.rocscience.com/help/slide2/documentation/knowledge-base/slip-surfaces-and-factor-of-safety"
                  target="_blank"
                  rel="noreferrer"
                >
                  www.rocscience.com/help/slide2/documentation/knowledge-base/slip-surfaces-and-factor-of-safety
                </a>
              </IconLi>
            </Box>
          </Alert>
        </div>
        <div className="theoryButtonContainer">
          <Link to="/calculations/slope-stability">
            <Button variant="outlined" size="small">
              GO TO CALCULATOR
            </Button>
          </Link>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default SlopeStabilityTheory;
